import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLogOut, FiSettings, FiCloud, FiChevronDown } = FiIcons;

const UserMenu = ({ onOpenSettings }) => {
  const { user, signOut, loading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (signingOut || loading) return;
    
    try {
      setSigningOut(true);
      console.log('🔄 UserMenu: Starting sign out...');
      
      const { error } = await signOut();
      if (error) {
        console.error('❌ Sign out error in UserMenu:', error);
        // Show user-friendly error but still close menu
        alert('Sign out error occurred, but you have been logged out locally.');
      }
      
      setIsOpen(false);
      console.log('✅ UserMenu: Sign out completed');
    } catch (error) {
      console.error('❌ Unexpected sign out error:', error);
      // Force close menu even on error
      setIsOpen(false);
    } finally {
      // Always reset signing out state
      setTimeout(() => {
        setSigningOut(false);
      }, 1000);
    }
  };

  if (!user) return null;

  const isLoading = loading || signingOut;

  return (
    <div className="relative">
      <button
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`flex items-center px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors duration-200 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mr-2">
          <SafeIcon icon={FiUser} className="text-white text-sm" />
        </div>
        <div className="text-left mr-2">
          <div className="text-white text-sm font-medium truncate max-w-32">
            {user.email?.split('@')[0]}
          </div>
          <div className="text-gray-400 text-xs">
            <SafeIcon icon={FiCloud} className="inline mr-1" />
            {isLoading ? 'Loading...' : 'Synced'}
          </div>
        </div>
        <SafeIcon 
          icon={FiChevronDown} 
          className={`text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          } ${isLoading ? 'animate-pulse' : ''}`} 
        />
      </button>

      <AnimatePresence>
        {isOpen && !isLoading && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu */}
            <motion.div
              className="absolute right-0 top-full mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-3 border-b border-gray-700">
                <div className="text-white text-sm font-medium">{user.email}</div>
                <div className="text-gray-400 text-xs">Angry Coder Account</div>
              </div>

              <div className="py-1">
                <button
                  onClick={() => {
                    onOpenSettings();
                    setIsOpen(false);
                  }}
                  disabled={signingOut}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-200 flex items-center disabled:opacity-50"
                >
                  <SafeIcon icon={FiSettings} className="mr-2" />
                  Settings
                </button>

                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors duration-200 flex items-center disabled:opacity-50"
                >
                  {signingOut ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                      Signing out...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiLogOut} className="mr-2" />
                      Sign Out
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserMenu;